/**
 * "Now" facts of the Lines panel (W2-07; DESIGN.md §4.10 "Now" column and the Visible now / Animating now filter
 * chips; E§3.6 LINE-03 RPE VisL / OnEve, E§4.15).
 *
 *  - Visible now: Phira draws something for the line at the playhead (`LineStateRt.drawsNothing === null`): its
 *    opacity is above 0, its scale is not 0, its text is not empty and it is not a UI mover. This comes from the
 *    engine's line states, which follow Phira's exact evaluation (factor before interpolation, f32 layer sums).
 *    Lines Phira draws nothing for keep the reason (row tooltip "Not drawn now: opacity 0").
 *  - Animating now: some property of the line is inside a segment that changes its value at the playhead: the
 *    segment has a next key, is not a "Stay, then jump" hold and its two ends differ.
 * Both ignore the preview-only filters (a hidden line is still "visible now" in the chart).
 *
 * `nowTracker(services)` keeps the facts current for the mounted panel: it polls the playhead four times a second
 * (the transport has no seek event while paused) and recomputes only when the time, the document or the engine
 * changed. Subscribers hear only about changed facts: during playback the time changes on every poll, but the sets
 * usually do not, and re-rendering the Lines panel four times a second cost about 40 ms per commit on 200 lines
 * (UX-053). The tracker is shared per app (keyed on the editor store, like linesUi).
 */

import type { AppServices } from '../../app/registry/types';
import type { Engine, LineStateRt } from '../../core/engine/types';
import type { ChartDoc, DocIndexes, Key, LineId, TrackValue } from '../../core/model/types';
import type { Sec } from '../../core/time/types';
import type { Unsubscribe } from '../../core/util/types';
import type { EditorStoreApi } from '../../state/types';
import type { NowFacts } from './lineModel';

export type HiddenReason = NonNullable<LineStateRt['drawsNothing']>;

export interface NowState extends NowFacts {
  /** Playhead time the facts were computed at (kept while later polls find the same facts). */
  readonly t: Sec;
  /** Engine revision the facts were computed at (-1 without an engine). */
  readonly revision: number;
  /** Why Phira draws nothing for a line at `t` (lines not in `visible`). */
  readonly hidden: ReadonlyMap<LineId, HiddenReason>;
}

export const EMPTY_NOW: NowState = Object.freeze({
  t: 0,
  revision: -1,
  visible: new Set<LineId>(),
  animating: new Set<LineId>(),
  hidden: new Map<LineId, HiddenReason>(),
});

function sameValue(a: TrackValue, b: TrackValue): boolean {
  if (typeof a === 'number' || typeof a === 'string') return a === b;
  if (typeof b === 'number' || typeof b === 'string') return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false;
  return true;
}

/** True when the segment starting at `key` changes the value (has a next key, is no hold, ends differ). */
export function segmentMoves(key: Key, next: Key | undefined): boolean {
  if (next === undefined) return false;
  if (key.ease.kind === 'hold') return false;
  return !sameValue(key.vOut, next.vIn);
}

/** Whether any line-owned track of `line` is inside a moving segment at `t`. */
export function lineAnimatingAt(engine: Engine, doc: ChartDoc, idx: DocIndexes, line: LineId, t: Sec): boolean {
  for (const tracks of idx.tracksOfLine(line).values()) {
    for (const track of tracks) {
      if (idx.keysByTrack(track).length < 2) continue;
      const startId = engine.segmentAt(track, t);
      if (startId === null) continue;
      const key = doc.keys.get(startId);
      if (key === undefined) continue;
      const nextId = idx.nextKey(startId);
      const next = nextId === undefined ? undefined : doc.keys.get(nextId);
      if (next === undefined) continue;
      // The segment is active only until the next key's time.
      if (t >= engine.tempo.secAt(next.t)) continue;
      if (segmentMoves(key, next)) return true;
    }
  }
  return false;
}

function sameSet<T>(a: ReadonlySet<T>, b: ReadonlySet<T>): boolean {
  if (a === b) return true;
  if (a.size !== b.size) return false;
  for (const v of a) if (!b.has(v)) return false;
  return true;
}

/** Whether two states hold the same facts (visible, animating, hidden reasons), whatever their times. */
export function sameNowFacts(a: NowState, b: NowState): boolean {
  if (a === b) return true;
  if (!sameSet(a.visible, b.visible) || !sameSet(a.animating, b.animating)) return false;
  if (a.hidden.size !== b.hidden.size) return false;
  for (const [id, why] of a.hidden) if (b.hidden.get(id) !== why) return false;
  return true;
}

/** Visible-now and animating-now sets of every line at `t`. */
export function computeNow(engine: Engine, doc: ChartDoc, idx: DocIndexes, t: Sec): NowState {
  const visible = new Set<LineId>();
  const animating = new Set<LineId>();
  const hidden = new Map<LineId, HiddenReason>();
  const states = engine.lineStates(t);
  // States are pooled: read what we need before the next query.
  for (const s of states) {
    if (!doc.lines.has(s.line)) continue;
    if (s.drawsNothing === null) visible.add(s.line);
    else hidden.set(s.line, s.drawsNothing);
  }
  for (const id of doc.lineOrder) if (lineAnimatingAt(engine, doc, idx, id, t)) animating.add(id);
  return { t, revision: engine.revision, visible, animating, hidden };
}

// ---------------------------------------------------------------------------------------------------------------
// Tracker
// ---------------------------------------------------------------------------------------------------------------

/** Playhead polling period (ms). */
export const NOW_POLL_MS = 250;

export interface NowTracker {
  get(): NowState;
  subscribe(cb: () => void): Unsubscribe;
  /** Recompute now (after a seek or an edit the caller knows about). */
  refresh(): void;
}

type NowServices = Pick<AppServices, 'editor' | 'store' | 'engine' | 'audio' | 'onChange'>;

function playhead(services: NowServices): Sec {
  try {
    return services.audio.transport.chartTime();
  } catch {
    return 0;
  }
}

function createTracker(current: () => NowServices): NowTracker {
  let state: NowState = EMPTY_NOW;
  let dirty = true;
  let lastT = Number.NaN;
  let lastStoreKey: unknown = null;
  const listeners = new Set<() => void>();
  let timer: ReturnType<typeof setInterval> | null = null;
  let offStore: Unsubscribe | null = null;
  let offServices: Unsubscribe | null = null;

  const watchStore = (): void => {
    offStore?.();
    offStore = null;
    const store = current().store;
    lastStoreKey = store;
    if (store !== null) {
      offStore = store.subscribe(() => {
        dirty = true;
      });
    }
  };

  const recompute = (force: boolean): void => {
    const services = current();
    if (services.store !== lastStoreKey) {
      watchStore();
      dirty = true;
    }
    const t = playhead(services);
    if (!force && !dirty && t === lastT) return;
    const store = services.store;
    const engine = services.engine;
    let next: NowState;
    if (store === null || engine === null) next = EMPTY_NOW;
    else {
      try {
        next = computeNow(engine, store.doc, store.idx, t);
      } catch {
        // A document change between the engine's sync and our read; try again on the next tick.
        return;
      }
    }
    dirty = false;
    lastT = t;
    // Same facts at another time (playback): keep the state object, so nobody re-renders.
    if (!force && sameNowFacts(next, state)) return;
    state = next;
    for (const cb of [...listeners]) cb();
  };

  return {
    get: () => state,
    subscribe(cb) {
      listeners.add(cb);
      if (listeners.size === 1) {
        watchStore();
        offServices = current().onChange(() => {
          dirty = true;
          recompute(false);
        });
        timer = setInterval(() => {
          recompute(false);
        }, NOW_POLL_MS);
        recompute(true);
      }
      return () => {
        listeners.delete(cb);
        if (listeners.size === 0) {
          if (timer !== null) clearInterval(timer);
          timer = null;
          offStore?.();
          offStore = null;
          offServices?.();
          offServices = null;
          lastStoreKey = null;
        }
      };
    },
    refresh() {
      recompute(true);
    },
  };
}

const trackers = new WeakMap<EditorStoreApi, { tracker: NowTracker; services: NowServices }>();

/** The shared "now" tracker of an app (it reads the services passed last, so project switches are seen). */
export function nowTracker(services: NowServices): NowTracker {
  let entry = trackers.get(services.editor);
  if (entry === undefined) {
    const holder: { tracker: NowTracker; services: NowServices } = {
      services,
      tracker: createTracker(() => holder.services),
    };
    entry = holder;
    trackers.set(services.editor, entry);
  }
  entry.services = services;
  return entry.tracker;
}
